/**
 * CLASS PERIODS, as a display grouping only.
 *
 * The only source of truth is the free-text `profiles.section` column written
 * by roster import, serialized per squad by the coach glance as `sections[]`
 * (raw strings, trimmed, deduped, sorted). Values in production look like "2",
 * "Period 2", "P2", and anything else a roster author typed.
 *
 * D1 RULING (plan v3, V3-2): the parser is CONSERVATIVE AND ANCHORED. It
 * recognizes exactly the anchored shapes as FULL-STRING, case-insensitive
 * matches. It performs NO arbitrary digit extraction from arbitrary strings and
 * NO guessing. Any string the anchored grammar does not match becomes its OWN
 * display group, rendered under its raw string, verbatim.
 *
 * WHY NO DIGIT EXTRACTION, stated plainly because it is the whole ruling: a
 * loose `/\d+/` would fold "Block 2", "Room 2", "2nd lunch" and "Period 2" into
 * one group named "period 2" and the facilitator would be filtering a room by a
 * grouping nobody authored. Three rooms silently becoming one is worse than
 * four groups, one of which is ugly. Ugly is honest.
 *
 * NOTHING HERE EVER WRITES A NORMALIZED VALUE. The raw string is preserved on
 * every group so any future write path (or any support question) still has the
 * roster's own text.
 */

/** The sentinel for "do not filter". Not a period, and never persisted as one. */
export const ALL_PERIODS = "all";

/**
 * The anchored grammar, longest alternative first so "period 2" cannot be
 * consumed by the "p" branch before the engine reaches it.
 *
 * Matches: "2", "P2", "p 2", "per 2", "per. 2", "per.2", "Period 2", "period  2".
 * Does NOT match: "2A", "Block 2", "Period Two", "2nd", "", "  ".
 */
const ANCHORED_PERIOD = /^(?:(?:period|per\.|per|p)\s*)?(\d{1,3})$/i;

/**
 * One raw section string in, one display group out.
 *
 * `key` is the grouping identity and the value that gets persisted. It is
 * namespaced (`period:` vs `raw:`) so a recognized "2" and an unrecognized
 * literal "period:2" typed into a roster can never collide.
 *
 * `raw` is always the string this group was derived from, so a group is never
 * separated from the text a human actually wrote.
 */
export const parsePeriod = (rawValue) => {
  const raw = typeof rawValue === "string" ? rawValue.trim() : "";
  if (raw === "") return null;

  const match = ANCHORED_PERIOD.exec(raw);
  if (match) {
    // Number(), not parseInt(): the capture group is already digits-only, and
    // this keeps "007" and "7" as the same group rather than two.
    const number = Number(match[1]);
    return {
      key: `period:${number}`,
      label: `period ${number}`,
      number,
      raw,
      recognized: true,
    };
  }

  return {
    key: `raw:${raw}`,
    // Verbatim. No lowercasing, no cleanup: this is somebody's typed text and
    // the point of the raw group is that it is recognizably theirs.
    label: raw,
    number: null,
    raw,
    recognized: false,
  };
};

/**
 * Whether one squad's `sections[]` belongs in the given group.
 *
 * A squad matches when ANY of its members' sections parse to that group, which
 * is the same rule the existing Students and Squads pages use. A squad whose
 * members sit in two periods therefore appears in both filtered views, and the
 * caller is responsible for saying so out loud (see FacilitatorContextBar's
 * spans-periods line). Appearing in two views silently is the failure this
 * comment exists to prevent.
 */
export const squadMatchesPeriod = (sections, periodKey) => {
  if (!periodKey || periodKey === ALL_PERIODS) return true;
  if (!Array.isArray(sections)) return false;
  return sections.some((section) => parsePeriod(section)?.key === periodKey);
};

/** How many distinct groups one squad's sections fall into. */
export const periodCount = (sections) => {
  if (!Array.isArray(sections)) return 0;
  const keys = new Set();
  sections.forEach((section) => {
    const parsed = parsePeriod(section);
    if (parsed) keys.add(parsed.key);
  });
  return keys.size;
};

/**
 * The option list for the selector, built from the union of every squad's
 * sections in the current envelope.
 *
 * Ordering: recognized periods ascending by number first, then unrecognized raw
 * groups in locale order. A facilitator scanning for "period 3" finds it where
 * they expect; the strings nobody could parse sit together at the end where
 * their oddness is visible rather than interleaved.
 */
export const periodOptions = (squads) => {
  const groups = new Map();
  (Array.isArray(squads) ? squads : []).forEach((squad) => {
    const sections = Array.isArray(squad?.sections) ? squad.sections : [];
    sections.forEach((section) => {
      const parsed = parsePeriod(section);
      if (parsed && !groups.has(parsed.key)) groups.set(parsed.key, parsed);
    });
  });

  return [...groups.values()].sort((a, b) => {
    if (a.recognized && b.recognized) return a.number - b.number;
    if (a.recognized) return -1;
    if (b.recognized) return 1;
    return a.label.localeCompare(b.label);
  });
};

/*
|------------------------------------------------------------------------------
| Session persistence
|------------------------------------------------------------------------------
| Per session, per tab, and per calendar day. A period chosen yesterday is not a
| statement about today's room: the same facilitator opening the tab the next
| morning teaches a different class first. The stored value is therefore stamped
| with the day it was chosen and discarded on read when the day has turned over.
|
| It is also stamped with the (cohort, challenge) it was chosen under, because
| "period 2" in one cohort is not "period 2" in another and restoring it across
| a context switch would filter the new room by the old room's grouping.
*/

export const PERIOD_STORAGE_KEY = "facilitator.shell.period";

/** Local calendar day, not UTC: the day boundary that matters is the teacher's. */
const today = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const contextStamp = (cohortId, challengeId) =>
  `${cohortId ?? ""}:${challengeId ?? ""}`;

/**
 * Reads the stored period, or null.
 *
 * Fails open to null on anything unexpected (storage unavailable, malformed
 * JSON, a stale shape from an older build). A facilitator whose stored filter
 * cannot be read sees the whole room, which is the safe direction: the failure
 * mode of the other direction is a room that looks emptier than it is.
 */
export const readStoredPeriod = (cohortId, challengeId) => {
  try {
    const stored = window.sessionStorage?.getItem(PERIOD_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed.key !== "string") return null;
    if (parsed.day !== today()) return null;
    if (parsed.context !== contextStamp(cohortId, challengeId)) return null;
    return parsed.key;
  } catch (e) {
    return null;
  }
};

export const writeStoredPeriod = (key, cohortId, challengeId) => {
  try {
    if (!key || key === ALL_PERIODS) {
      window.sessionStorage?.removeItem(PERIOD_STORAGE_KEY);
      return;
    }
    window.sessionStorage?.setItem(
      PERIOD_STORAGE_KEY,
      JSON.stringify({
        key,
        day: today(),
        context: contextStamp(cohortId, challengeId),
      }),
    );
  } catch (e) {
    /* storage unavailable: the in-memory selection still holds for this view */
  }
};

/** Called by the central signout. A period is a per-person view preference. */
export const clearStoredPeriod = () => {
  try {
    window.sessionStorage?.removeItem(PERIOD_STORAGE_KEY);
  } catch (e) {
    /* nothing to clear */
  }
};
